/**
 * MUI SVGContainer Component
 * 
 * This component is the Material UI equivalent of the original SVGContainer component.
 * It renders an SVG container for the Room Designer application.
 */

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { uiReducers, getSVGBoundingBox, getHearSpaceLoading } from 'reduxModules/ducks/ui';
import DrawManager from 'components/services/DrawManager';
import MouseHandler from 'components/services/MouseHandler';
import HeatMapManager from 'components/services/HeatMapManager';
import LoadingOverlay from 'components/atoms/LoadingOverlay';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * Interface for SVG bounding box dimensions
 */
interface SVGBoundingBox {
  width: number;
  height: number;
  top?: number;
  left?: number;
}

/**
 * MUI SVGContainer component
 * 
 * @param props - Component props
 * @returns The MUI SVGContainer component
 */
const MuiSVGContainer: React.FC = () => {
  // Get dimension of the container
  const refContainer = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [container, setContainer] = useState<SVGBoundingBox>(
    useSelector((state: any) => (getSVGBoundingBox as any)(state)) as SVGBoundingBox
  );

  // Track if we're currently dragging to prevent resizing
  const currentAction = useSelector((state: any) => state.ui.action) as string | undefined;
  const isDragging = useRef(false);

  // Get HearSpace loading state
  const hearSpaceLoading = useSelector(getHearSpaceLoading);

  // Set dragging state based on current action
  useEffect(() => {
    // Include all actions that involve clicking/dragging on the canvas
    isDragging.current = ['move', 'add', 'remove', 'position', 'distance', 'level', 'relativelevel', 'block', 'armedghostchair', 'ghostchair'].includes(currentAction || '');
  }, [currentAction]);

  // Function to update SVG container size
  const updateSize = useCallback(() => {
    // Don't update size if we're dragging
    if (isDragging.current) {
      return;
    }

    const bBox = refContainer.current?.getBoundingClientRect();
    if (bBox) {
      // Check if the size actually changed before updating
      // This prevents unnecessary updates during drag operations
      const newWidth = Math.round(bBox.width);
      const newHeight = Math.round(bBox.height);
      
      if (container.width !== newWidth || container.height !== newHeight) {
        const newContainer = {
          width: newWidth,
          height: newHeight
        };
        setContainer(newContainer);
        // Cast to AnyAction since we don't have exact action type
        dispatch(uiReducers.setSvgBoundingBox({ 
          top: bBox.top, 
          left: bBox.left, 
          width: newWidth, 
          height: newHeight 
        }) as unknown as AnyAction);
      }
    }
  }, [dispatch, container.width, container.height]);

  // Recalculate dimensions on mount and when window resizes
  useEffect(() => {
    if (refContainer.current) {
      // Initial calculation
      updateSize();
      
      // Add resize listener
      window.addEventListener('resize', updateSize);
      
      // Use ResizeObserver for more reliable size detection with debouncing
      let resizeTimeout: NodeJS.Timeout;
      const resizeObserver = new ResizeObserver((entries) => {
        // Don't process resize events if we're dragging
        if (isDragging.current) {
          return;
        }
        
        // Debounce resize observations to prevent excessive updates
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Only update if the element is still connected to the DOM and not dragging
          if (entries[0]?.target && document.contains(entries[0].target) && !isDragging.current) {
            updateSize();
          }
        }, 100); // 100ms debounce
      });
      
      resizeObserver.observe(refContainer.current);
      
      return () => {
        window.removeEventListener('resize', updateSize);
        clearTimeout(resizeTimeout);
        resizeObserver.disconnect();
      };
    }
  }, [updateSize]);

  // Only force recalculation on initial mount, not on every render
  useLayoutEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully updated
    const rafId = requestAnimationFrame(() => {
      updateSize();
    });
    
    return () => {
      cancelAnimationFrame(rafId);
    };
    // Remove updateSize from dependencies to prevent updates on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return (
    <Box 
      ref={refContainer} 
      sx={{ 
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        position: 'relative',
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0
      }}
    >
      {/* Render HeatMapManager as a background layer behind the SVG */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      >
        <HeatMapManager />
      </Box>
      
      {/* SVG layer on top */}
      <Box
        component="svg"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
          position: 'relative',
          zIndex: 100
        }}
      >
        {/* Cast to any to avoid TypeScript errors with component types */}
        <Box component={MouseHandler as any}>
          <Box component={DrawManager as any} container={container} />
        </Box>
      </Box>

      {/* Loading overlay for HearSpace API calls */}
      <LoadingOverlay visible={hearSpaceLoading} label="Calculating..." size={40} />

    </Box>
  );
};

export default MuiSVGContainer;
